import React, { useState } from "react";
import { Check, RotateCcw, Save } from "lucide-react";
import { Combobox } from "../ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  FEEDBACK_MAX_CHARS,
  IN_CATEGORY_FIELDS,
  TAG_FIELDS,
  editorDefaultState,
  handleCategoryToken,
  REASONING_KEY_FOR_FIELD,
  handleCleanNotes,
  handleReasonLabel,
  handleTagValues,
} from "../../utils/constants/AdIntelligence";
import { Button } from "../ui/button";
import useTaxonomyOptions from "../../hooks/useTaxonomyOptions";
import AudienceReview from "./AudienceReview";
import FieldNote from "./FieldNote";
import ModelNote from "./ModelNote";

const FieldSelect = ({ value, options, onChange, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="h-9 bg-white text-sm">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** The tag's fields, editable in place, with a reason per change, a note per field, feedback and the verdict buttons. */
const TagEditor = ({
  tag,
  options,
  optionsFailed,
  canApprove,
  hasNext,
  isSaving,
  onApprove,
  onSave,
}) => {
  const [editor, setEditor] = useState(() => ({
    ...editorDefaultState,
    values: handleTagValues({ tag }),
    notes: tag.review_notes || {},
    feedback: tag.feedback || "",
  }));
  const { values, reasons, notes, feedback } = editor;
  const targetKey = options?.categories?.find(
    (c) =>
      handleCategoryToken({ label: c.label }) ===
      handleCategoryToken({ label: values.pitched_category })
  )?.key;
  const isMoved = !!targetKey && targetKey !== tag.category;
  const { data: targetOptions } = useTaxonomyOptions({
    category: targetKey,
    enabled: isMoved,
  });
  const original = handleTagValues({ tag });
  const handleModelReason = ({ key }) =>
    (tag.reasoning || {})[REASONING_KEY_FOR_FIELD[key]] || "";
  const config = options?.categories?.find((c) => c.key === tag.category);
  const targetConfig = isMoved
    ? options?.categories?.find((c) => c.key === targetKey)
    : config;
  const vocabulary = (isMoved ? targetOptions : options)?.vocabulary ?? {
    topics: [],
    pains: [],
  };
  const otherReason = options?.other_reason || "other";
  const reasonCodes = [
    ...(config?.reason_codes ?? []).filter((c) => c !== otherReason),
    otherReason,
  ];
  const categoryChanged = values.pitched_category !== original.pitched_category;
  const exited = !!values.pitched_category && !targetKey;
  const effective = exited
    ? {
        ...values,
        ...Object.fromEntries(IN_CATEGORY_FIELDS.map((k) => [k, ""])),
      }
    : values;
  const changed = TAG_FIELDS.map(({ key }) => key).filter(
    (key) => effective[key] !== original[key]
  );
  const shownFields = TAG_FIELDS.filter(
    ({ key }) => !exited || !IN_CATEGORY_FIELDS.includes(key)
  );
  const topicOptions = [
    ...new Set(
      vocabulary.topics
        .filter(
          (t) =>
            !values.pitched_subcategory ||
            t.subcategory === values.pitched_subcategory
        )
        .map((t) => t.text)
    ),
  ].map((text) => ({ value: text, label: text }));
  const painOptions = vocabulary.pains.map((p) => ({ value: p, label: p }));
  const isTopicListed = vocabulary.topics.some(
    (t) =>
      t.subcategory === values.pitched_subcategory &&
      t.text.toLowerCase() === (values.pitched_topic || "").toLowerCase()
  );
  const topicNote =
    exited || !values.pitched_topic || isTopicListed
      ? ""
      : values.pitched_topic === original.pitched_topic && !categoryChanged
        ? "The model made up this topic. It isn't in the approved word list yet, and the owner decides whether to add it."
        : "Not in the approved word list. Saving proposes it to the owner.";
  const handleInheritsReason = ({ key }) =>
    categoryChanged && IN_CATEGORY_FIELDS.includes(key);
  const handleReasonFor = ({ key }) =>
    reasons[key] ||
    (handleInheritsReason({ key }) ? reasons.pitched_category : "");
  const handleNoteKeyFor = ({ key }) =>
    handleInheritsReason({ key }) ? "pitched_category" : key;
  const handleNeedsNote = ({ key }) =>
    handleReasonFor({ key }) === otherReason &&
    !(notes[handleNoteKeyFor({ key })] || "").trim();
  const isMissingNote = changed.some((key) => handleNeedsNote({ key }));
  const isMissingReason = changed.some((key) => !handleReasonFor({ key }));
  const isModified = changed.length > 0;
  const isIncomplete =
    !values.pitched_category ||
    !values.pitch_type ||
    (!exited && (!values.pitched_subcategory || !values.pitched_topic));
  const needsReview = tag.status === "needs_review";

  const handleSetValue = ({ key, value }) =>
    setEditor((prev) => {
      if (key !== "pitched_category") {
        return { ...prev, values: { ...prev.values, [key]: value } };
      }
      const backHome = value === original.pitched_category;
      const inCategory = Object.fromEntries(
        IN_CATEGORY_FIELDS.map((k) => [k, backHome ? original[k] : ""])
      );
      return {
        ...prev,
        values: { ...prev.values, ...inCategory, [key]: value },
      };
    });

  const handleSetNote = ({ key, text }) =>
    setEditor((prev) => ({ ...prev, notes: { ...prev.notes, [key]: text } }));

  const handleSetReason = ({ key, code }) =>
    setEditor((prev) => ({
      ...prev,
      reasons: { ...prev.reasons, [key]: code },
    }));

  const handleReset = () =>
    setEditor((prev) => ({
      ...prev,
      values: handleTagValues({ tag }),
      reasons: {},
    }));

  const handleSave = () =>
    onSave({
      fields: Object.fromEntries(changed.map((k) => [k, effective[k]])),
      reasons: Object.fromEntries(
        changed.map((k) => [k, handleReasonFor({ key: k })])
      ),
      feedback: feedback.trim(),
      notes: handleCleanNotes({ notes }),
    });

  const handleControl = ({ key }) => {
    if (key === "pitched_category") {
      return (
        <Combobox
          value={values.pitched_category}
          options={(options?.category_labels ?? []).map((l) => ({
            value: l,
            label: l,
          }))}
          onChange={(value) => handleSetValue({ key, value })}
          placeholder="Pick a category"
        />
      );
    }
    if (key === "pitched_subcategory") {
      return (
        <FieldSelect
          value={values.pitched_subcategory}
          options={(targetConfig?.subcategories ?? []).map((s) => ({
            value: s,
            label: s,
          }))}
          onChange={(value) => handleSetValue({ key, value })}
          placeholder="Pick a sub-category"
        />
      );
    }
    if (key === "pitched_topic") {
      return (
        <Combobox
          value={values.pitched_topic}
          options={topicOptions}
          onChange={(value) => handleSetValue({ key, value })}
          placeholder="Pick or type a topic"
          allowCreate
          createLabel={(text) => `New topic: "${text}"`}
        />
      );
    }
    if (key === "pain_point") {
      return (
        <div className="flex items-center gap-2">
          <Combobox
            value={values.pain_point}
            options={painOptions}
            onChange={(value) => handleSetValue({ key, value })}
            placeholder="No pain point"
            allowCreate
            createLabel={(text) => `New pain point: "${text}"`}
          />
          {values.pain_point && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 text-gray-500"
              onClick={() => handleSetValue({ key, value: "" })}
            >
              Clear
            </Button>
          )}
        </div>
      );
    }
    return (
      <FieldSelect
        value={values.pitch_type}
        options={(options?.pitch_types ?? []).map((p) => ({
          value: p,
          label: p,
        }))}
        onChange={(value) => handleSetValue({ key, value })}
        placeholder="Pick a pitch type"
      />
    );
  };

  if (!config) {
    return (
      <div className="space-y-3">
        <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2 text-sm">
          {TAG_FIELDS.map(({ key, label }) => (
            <React.Fragment key={key}>
              <dt className="text-xs font-medium text-gray-500">{label}</dt>
              <dd className="text-gray-900">
                {original[key] || "—"}
                <ModelNote text={handleModelReason({ key })} />
              </dd>
            </React.Fragment>
          ))}
        </dl>
        <AudienceReview audience={tag.audience} reasoning={tag.reasoning} />
        <p
          className={`text-xs ${optionsFailed ? "text-rose-700" : "text-gray-500"}`}
        >
          {optionsFailed
            ? "Couldn't load this category's options, so the tag can't be edited or approved here."
            : "Loading the category's options…"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[120px_1fr] sm:items-start">
        {shownFields.map(({ key, label }) => (
          <React.Fragment key={key}>
            <p className="pt-2 text-xs font-medium text-gray-500">{label}</p>
            <div
              className={
                changed.includes(key)
                  ? "rounded-lg bg-[#f6f5fe] p-1.5 ring-1 ring-[#7f7ad5]"
                  : ""
              }
            >
              {handleControl({ key })}
              {!changed.includes(key) && (
                <ModelNote text={handleModelReason({ key })} />
              )}
              {changed.includes(key) && (
                <div className="mt-1.5 space-y-1 px-1">
                  <p className="text-xs text-[#3c3489]">
                    Was: {original[key] || "empty"}
                  </p>
                  {!handleInheritsReason({ key }) && (
                    <FieldSelect
                      value={reasons[key]}
                      options={reasonCodes.map((code) => ({
                        value: code,
                        label: handleReasonLabel({ code }),
                      }))}
                      onChange={(code) => handleSetReason({ key, code })}
                      placeholder="Why change it? Pick a reason"
                    />
                  )}
                </div>
              )}
              {key === "pitched_topic" && topicNote && (
                <p className="mt-1 px-1 text-xs text-amber-700">{topicNote}</p>
              )}
              <FieldNote
                value={notes[key]}
                required={
                  changed.includes(key) &&
                  handleReasonFor({ key }) === otherReason &&
                  !handleInheritsReason({ key })
                }
                onChange={(text) => handleSetNote({ key, text })}
              />
            </div>
          </React.Fragment>
        ))}
      </div>
      {exited && (
        <p className="text-xs text-gray-500">
          {values.pitched_category} has no rulebook here, so the ad is marked as
          belonging there and its sub-category, topic and pain point are
          cleared.
        </p>
      )}
      {isMoved && (
        <p className="text-xs text-gray-500">
          The tag moves to {values.pitched_category} and is checked against its
          rules. The category&apos;s reason covers the sub-category, topic and
          pain point.
        </p>
      )}
      <AudienceReview
        audience={tag.audience}
        reasoning={tag.reasoning}
        notes={notes}
        onNoteChange={handleSetNote}
      />
      <div>
        <p className="mb-1 text-xs font-medium text-gray-500">
          Feedback <span className="font-normal text-gray-400">(optional)</span>
        </p>
        <textarea
          value={feedback}
          onChange={(e) =>
            setEditor((prev) => ({ ...prev, feedback: e.target.value }))
          }
          rows={3}
          maxLength={FEEDBACK_MAX_CHARS}
          placeholder={
            isModified
              ? "Anything the reasons don't capture"
              : "Anything worth noting about this tag"
          }
          className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
        {isModified && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleReset}
            disabled={isSaving}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        )}
        {isModified || !canApprove ? (
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              !isModified ||
              isIncomplete ||
              isMissingReason ||
              isMissingNote ||
              isSaving
            }
            className="bg-[#372e5e] text-white hover:bg-[#2a2349]"
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Saving…" : "Save changes"}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() =>
              onApprove({
                feedback: feedback.trim(),
                notes: handleCleanNotes({ notes }),
              })
            }
            disabled={needsReview || isSaving}
            className="bg-[#372e5e] text-white hover:bg-[#2a2349]"
          >
            <Check className="h-4 w-4" />
            {hasNext ? "Approve and next" : "Approve"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default TagEditor;
